import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/features/auth/hooks/useAuth";
import { RBAC_STALE_TIME, rbacKeys } from "@/features/rbac/hooks/rbacKeys";
import type { RbacAssignment } from "@/features/rbac/schemas/rbac";
import {
  getAssignment,
  listAssignments,
  setAssignment,
  type SetAssignmentInput,
} from "@/features/rbac/services/assignmentsService";

/** Asignación de una persona concreta. */
export function useAssignment(subjectUid: string | null) {
  return useQuery<RbacAssignment | null>({
    queryKey: rbacKeys.assignment(subjectUid),
    queryFn: () => (subjectUid ? getAssignment(subjectUid) : Promise.resolve(null)),
    enabled: subjectUid !== null,
    staleTime: RBAC_STALE_TIME,
  });
}

/** Todas las asignaciones explícitas. Solo se usa en la pantalla de Roles. */
export function useAssignments(enabled = true) {
  return useQuery<RbacAssignment[]>({
    queryKey: rbacKeys.assignments(),
    queryFn: listAssignments,
    enabled,
    staleTime: RBAC_STALE_TIME,
  });
}

export function useSetAssignment() {
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();

  return useMutation({
    mutationFn: (input: SetAssignmentInput) =>
      setAssignment(input, currentUser?.uid ?? null),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: rbacKeys.all });
    },
  });
}
